import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, KeyRound, LogOut } from 'lucide-react';
import { TwistedCrest, PentagramMark } from '../components/Crest';
import { API, LOCATIONS, DARK_MOTTO } from '../data/content';

export default function Archive() {
  const [access, setAccess] = useState('checking');
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    fetch(`${API}/archive/status`, { credentials: 'include' })
      .then((r) => setAccess(r.ok ? 'unlocked' : 'locked'))
      .catch(() => setAccess('locked'));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${API}/archive/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase }),
      });
      if (!r.ok) throw new Error('denied');
      setAccess('unlocked');
    } catch {
      setError('The register does not know you.');
      setAttempts((a) => a + 1);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/archive/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    setAccess('locked');
    setPassphrase('');
  };

  return (
    <div className="sanctum min-h-screen bg-obsidian text-gray-200 relative overflow-hidden" data-testid="archive-page">
      <div className="grain-overlay" />
      <div className="pointer-events-none fixed -left-40 -top-40 w-[34rem] h-[34rem] text-crimson/15 animate-slow-rotate" aria-hidden="true">
        <PentagramMark />
      </div>
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,7,100,0.25),transparent_55%)]" aria-hidden="true" />

      <header className="relative z-10 border-b border-occult-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TwistedCrest className="w-8 h-10" />
            <div className="leading-none">
              <p className="font-serif font-semibold text-lg tracking-wide text-gray-100">THE CONCLAVE ARCHIVE</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-crimson mt-1">{DARK_MOTTO}</p>
            </div>
          </div>
          <Link
            to="/"
            data-testid="archive-back-button"
            className="group inline-flex items-center gap-2 font-display text-xs font-semibold tracking-[0.2em] uppercase text-gray-400 hover:text-occult-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Return to the surface
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 pb-28">
        {access !== 'unlocked' ? (
          <div className="pt-24 lg:pt-32 flex flex-col items-center text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="font-mono text-[10px] sm:text-xs tracking-[0.4em] uppercase text-blood mb-6">
                Clearance required
              </p>
              <h1 className="font-serif font-medium text-4xl sm:text-5xl text-gray-100 leading-tight max-w-xl">
                The Archive answers only to its key.
              </h1>
              <form onSubmit={submit} className="mt-12 w-80 sm:w-96" data-testid="archive-login-form">
                <div className="flex items-center gap-3 border border-occult-border bg-occult-surface px-4 focus-within:border-occult-gold/60 transition-colors">
                  <KeyRound className="w-4 h-4 text-occult-gold shrink-0" />
                  <input
                    type="password"
                    data-testid="archive-passkey-input"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    placeholder="Speak the pass key"
                    autoComplete="off"
                    className="w-full bg-transparent py-3.5 font-mono text-sm tracking-widest text-gray-100 placeholder:text-gray-600 focus:outline-none"
                  />
                </div>
                {error && (
                  <motion.p
                    data-testid="archive-error"
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: [0, -5, 5, -3, 0] }}
                    transition={{ duration: 0.4 }}
                    className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-blood"
                  >
                    {error}
                  </motion.p>
                )}
                <button
                  type="submit"
                  data-testid="archive-unlock-button"
                  disabled={busy || !passphrase}
                  className="mt-6 w-full border border-occult-gold/50 py-3.5 font-display text-xs font-semibold tracking-[0.3em] uppercase text-occult-gold hover:bg-occult-gold hover:text-obsidian transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? 'Consulting the register…' : access === 'checking' ? 'Consulting the register…' : 'Unseal the Archive'}
                </button>
              </form>

              {attempts >= 3 && (
                <motion.div
                  data-testid="archive-warning-note"
                  initial={{ opacity: 0, y: 12, rotate: 0 }}
                  animate={{ opacity: 1, y: 0, rotate: -2.5 }}
                  transition={{ duration: 0.7, delay: 0.25 }}
                  className="mt-10 max-w-xs border-l-2 border-blood/70 pl-4 text-left"
                >
                  <p className="font-serif italic text-lg text-blood/90 leading-snug">
                    Third error. The register has marked your hand.
                  </p>
                  <p className="mt-2 font-serif italic text-sm text-gray-500 leading-snug">
                    Do not knock again without the key. We know the sound of your knock now.
                  </p>
                  <p className="mt-3 font-mono text-[9px] tracking-[0.3em] uppercase text-blood/70">
                    — The Conclave
                  </p>
                </motion.div>
              )}

              {attempts >= 4 && (
                <motion.div
                  data-testid="archive-warning-note-2"
                  initial={{ opacity: 0, y: 12, rotate: 0 }}
                  animate={{ opacity: 1, y: 0, rotate: 1.8 }}
                  transition={{ duration: 0.7, delay: 0.25 }}
                  className="mt-6 max-w-xs border-l-2 border-blood pl-4 text-left"
                >
                  <p className="font-serif italic text-xl text-blood leading-snug">
                    Fourth. You are trying our patience.
                  </p>
                  <p className="mt-2 font-serif italic text-sm text-gray-500 leading-snug">
                    Our patience is older than your country. Do not test which of us tires first.
                  </p>
                  <p className="mt-3 font-mono text-[9px] tracking-[0.3em] uppercase text-blood">
                    — The Conclave, less cordially
                  </p>
                </motion.div>
              )}

              {attempts >= 5 && (
                <motion.div
                  data-testid="archive-warning-note-3"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mt-6 max-w-xs border-2 border-blood/80 bg-crimson/10 px-5 py-4 text-left rotate-[-1deg] sanctum-glow"
                >
                  <p className="font-serif italic font-semibold text-2xl text-blood leading-snug text-flicker">
                    ENOUGH.
                  </p>
                  <p className="mt-2 font-serif italic text-sm text-gray-400 leading-snug">
                    The next wrong knock will be answered by name. We are very good at names.
                  </p>
                  <p className="mt-3 font-mono text-[9px] tracking-[0.3em] uppercase text-blood">
                    — O. León, Mayor of St. Bellmore
                  </p>
                </motion.div>
              )}

              <p className="mt-8 font-serif italic text-sm text-gray-600 max-w-xs">
                Forget you found this door.
              </p>
            </motion.div>
          </div>
        ) : (
          <div className="pt-16 lg:pt-24">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                  <p className="font-mono text-[10px] sm:text-xs tracking-[0.4em] uppercase text-blood mb-6">
                    Register opened · Clearance: Initiate
                  </p>
                  <h1 className="font-serif font-medium text-4xl sm:text-5xl text-gray-100 leading-tight">
                    Nine records, unsealed.
                  </h1>
                </div>
                <button
                  type="button"
                  data-testid="archive-logout-button"
                  onClick={logout}
                  className="inline-flex items-center gap-2 border border-occult-border px-5 py-2.5 font-display text-[10px] font-semibold tracking-[0.25em] uppercase text-gray-400 hover:text-blood hover:border-blood/60 transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Seal the archive
                </button>
              </div>

              <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-occult-border border border-occult-border">
                {LOCATIONS.map((loc, i) => (
                  <motion.div
                    key={loc.id}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.6 }}
                  >
                    <Link
                      to={`/sanctum/${loc.id}`}
                      data-testid={`archive-entry-${loc.id}`}
                      className="group block h-full bg-obsidian hover:bg-occult-subtle transition-colors duration-300 p-7"
                    >
                      <div className="flex items-start justify-between">
                        <span className="font-mono text-[10px] tracking-[0.3em] text-blood">{loc.entry}</span>
                        <span className="w-6 h-px bg-occult-gold/40 transition-all duration-500 group-hover:w-10 group-hover:bg-occult-gold" />
                      </div>
                      <p className="mt-8 font-serif italic text-lg text-gray-200 group-hover:text-occult-gold transition-colors leading-snug">
                        {loc.name}
                      </p>
                      <p className="mt-2 font-mono text-[9px] tracking-[0.22em] uppercase text-gray-500 leading-relaxed">
                        {loc.secretTitle}
                      </p>
                      <p className="mt-5 font-mono text-[9px] tracking-[0.25em] uppercase text-gray-600">
                        Keeper · {loc.keeper}
                      </p>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <p className="mt-16 text-center font-mono text-[9px] tracking-[0.4em] uppercase text-gray-700">
                {DARK_MOTTO} · What is read below remains below
              </p>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
}
